import { useEffect, useMemo, useRef } from 'react'
import { ApolloError } from 'apollo-client'
import { Stripe, StripeElements } from '@stripe/stripe-js'
import { CardNumberElement } from '@stripe/react-stripe-js'
import { GraphQLError } from 'graphql'

// Utils
import { noop, castError } from '@sketch/utils'
import {
  confirmPendingSetupIntent,
  updateWorkspaceTosAgreed,
} from '../../utils'

// Types
import {
  useSubscribeCustomerToPlanMutation,
  useApplyCouponMutation,
  GetWorkspaceDocument,
} from '@sketch/gql-types'
import { SubscribeToPlanWithBilling } from '../../types'

interface Listeners {
  onComplete: () => void
  onError: (error: Error) => void
}

export const useSubscribeToPlan = (
  workspaceId: string,
  stripe?: Stripe,
  elements?: StripeElements,
  listeners?: Listeners
) => {
  const cachedListeners = useRef(listeners)

  useEffect(() => {
    cachedListeners.current = listeners
  }, [listeners])

  const [applyCoupon] = useApplyCouponMutation({
    onError: 'unsafe-throw-exception',
  })

  const [subscribe, { data }] = useSubscribeCustomerToPlanMutation({
    onError: 'unsafe-throw-exception',
    update: (cache, { data }) => {
      /* Some error prevented the customer to be queried */
      if (!data?.subscribeCustomerToPlan?.customer) {
        return
      }

      updateWorkspaceTosAgreed(cache, { workspaceId, tosAgreed: true })
    },
    refetchQueries: [
      {
        // Workspace subscribed successfuly, so lets update its status
        // this allows expired workspaces to show the correct empty state
        // after the subscribe
        query: GetWorkspaceDocument,
        variables: { identifier: workspaceId },
      },
    ],
  })

  const subscribeCustomer = useMemo(() => {
    const action = async (
      customerId: string,
      state: SubscribeToPlanWithBilling,
      tos: boolean
    ) => {
      try {
        if (!stripe || !elements) {
          throw new Error('Stripe module is not available')
        }

        const cardElement = elements.getElement(CardNumberElement)
        const { error, token } = await stripe.createToken(cardElement!, {
          name: state.billing.cardName,
        })

        if (error || !token) {
          /**
           *  We opted by mimicking a ApolloError, since we already have
           *  a structure in place from BE to represent the error in the
           *  correct field. This way we prevent having an additional
           *  error entity that handles distinguishes these errors.
           */
          throw new ApolloError({
            graphQLErrors: [
              new GraphQLError(
                error?.message || "It wasn't possible to use your credit-card",
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                { stripeErrorCode: error?.code }
              ),
            ],
          })
        }

        const { plan, billing, editors } = state
        const {
          name,
          email,
          address,
          cardName,
          taxId,
          ...otherBilling
        } = billing

        const mutationResponse = await subscribe({
          variables: {
            input: {
              customerId,
              planId: plan.id,
              paymentSource: token.id,
              tosAgreed: tos,
              billingDetails: {
                name,
                email,
                taxId,
                address: {
                  line1: address,
                  ...otherBilling,
                },
              },
              seats: editors,
            },
          },
        })

        const { pendingScaToken } =
          mutationResponse?.data?.subscribeCustomerToPlan || {}

        if (pendingScaToken) {
          await confirmPendingSetupIntent(stripe, pendingScaToken)
        }

        // We actually apply the coupon code here
        if (state.discountCode) {
          await applyCoupon({
            variables: {
              customerId,
              promotionCode: state.discountCode,
            },
          }).catch(noop) // We want to ignore applyCoupon errors
        }

        cachedListeners.current?.onComplete()
      } catch (e) {
        const error = castError(e)
        // We wanna make sure the onError listener is called for
        // all errors occurred in this call
        cachedListeners.current?.onError?.(error)

        // But we also wanna propagate the errors to the
        // function callers because they might wanna do some
        // specific handling like Inline field error
        throw error
      }
    }

    return action
  }, [applyCoupon, elements, stripe, subscribe])

  return [subscribeCustomer, data] as const
}
